// Sources tab: the roadmap's source ledger (kanban 83428bab) — what Merlin was
// handed and what became of each source, so "did Merlin read X?" has an answer
// on the page instead of in a transcript. Grouped by outcome, waiting items
// first: unread and deferred are what the NEXT run will look at; read and
// skipped are settled (skipped carries Merlin's reason).

import { useMemo, useState } from 'react';
import type { RoadmapSource, RoadmapSourceListResponse, RoadmapSourceOutcome } from '../../services/roadmapReports';
import { KIND_LABELS, shortId, sourceLabel, triageLabel } from './sourcesPanelUtils';
import styles from './RoadmapV2.module.css';

interface Props {
  data: RoadmapSourceListResponse;
  onSelect: (key: string) => void;
  /** Initiative titles by key, so a source says what it was used for in words. */
  titles?: Record<string, string>;
}

const ORDER: RoadmapSourceOutcome[] = ['unread', 'deferred', 'read', 'skipped'];

const TITLES: Record<RoadmapSourceOutcome, string> = {
  unread: 'Waiting for the next run',
  deferred: 'Seen, not yet used (listed again next run)',
  read: 'Read and used',
  skipped: 'Considered and set aside',
};


export function SourcesPanel({ data, onSelect, titles = {} }: Props) {
  const [only, setOnly] = useState<RoadmapSourceOutcome | 'all'>('all');
  const groups = useMemo(() => {
    const by = new Map<RoadmapSourceOutcome, RoadmapSource[]>();
    for (const o of ORDER) by.set(o, []);
    for (const s of data.items) by.get(s.outcome)?.push(s);
    return by;
  }, [data.items]);

  const shown = ORDER.filter((o) => only === 'all' || o === only);

  return (
    <section className={styles.panel} aria-label="Source ledger">
      <div className={styles.panelHead}>
        <h2>Sources</h2>
        <span className={styles.meta}>
          {data.total} source{data.total === 1 ? '' : 's'} on the ledger · every run stamps what it read, skipped or left for next time
          {(data.summary.held_by_triage ?? 0) > 0 && (
            <span data-testid="sources-held-by-triage">
              {' '}· {data.summary.held_by_triage} recording{data.summary.held_by_triage === 1 ? '' : 's'} held back by triage, waiting for a run that happens for another reason
            </span>
          )}
        </span>
      </div>
      <div className={styles.chips} role="group" aria-label="Filter by outcome">
        <button
          type="button"
          className={`${styles.chip} ${only === 'all' ? styles.chipOn : ''}`}
          onClick={() => setOnly('all')}
        >
          All {data.total}
        </button>
        {ORDER.map((o) => (
          <button
            key={o}
            type="button"
            className={`${styles.chip} ${only === o ? styles.chipOn : ''}`}
            onClick={() => setOnly(only === o ? 'all' : o)}
            data-testid={`sources-filter-${o}`}
          >
            {TITLES[o]} {data.summary[o] ?? 0}
          </button>
        ))}
      </div>
      {shown.map((o) => {
        const rows = groups.get(o) ?? [];
        return (
          <div key={o} className={styles.section} data-testid={`sources-${o}`}>
            <h3>
              {TITLES[o]} <span className={styles.count}>{rows.length}</span>
            </h3>
            {rows.length === 0 ? (
              <p className={styles.muted}>Nothing here.</p>
            ) : (
              <ul className={styles.plainList}>
                {rows.map((s) => (
                  <li key={s.id}>
                    <span className={styles.badge}>{KIND_LABELS[s.source_kind]}</span>{' '}
                    <strong title={s.last_run_id ? `last looked at by run ${shortId(s.last_run_id)}` : undefined}>{sourceLabel(s)}</strong>
                    {s.initiative_keys.length > 0 && (
                      <span className={styles.meta}>
                        {' '}
                        · used for{' '}
                        {s.initiative_keys.map((k, i) => (
                          <span key={k}>
                            {i > 0 ? ', ' : ''}
                            <button type="button" className={styles.linkBtn} onClick={() => onSelect(k)}>
                              {titles[k] ?? k}
                            </button>
                          </span>
                        ))}
                      </span>
                    )}
                    {s.reason && <div className={styles.muted}>{s.reason}</div>}
                    {triageLabel(s) && (
                      <div className={styles.muted} data-testid={`sources-triage-${s.id}`} title={s.triage?.extract ?? undefined}>
                        {triageLabel(s)}
                        {s.triage?.reason ? ` — ${s.triage.reason}` : ''}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </section>
  );
}
